import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { PartyPopper, Send, Settings2, Plus, Trash2, Check, Loader2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WishesPage = () => {
  const [config, setConfig] = useState(null);
  const [todayWishes, setTodayWishes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [editConfig, setEditConfig] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [configRes, wishesRes] = await Promise.all([
        axios.get(`${API_URL}/api/wishes/config`, { withCredentials: true }),
        axios.get(`${API_URL}/api/wishes/today`, { withCredentials: true }),
      ]);
      setConfig(configRes.data);
      setTodayWishes(wishesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendWish = async (wish) => {
    setSending(wish.employee_id + wish.type);
    try {
      let message = (wish.template || '').replace('{name}', wish.name);
      let title;
      if (wish.type === 'birthday') {
        title = 'Happy Birthday!';
      } else if (wish.type === 'marriage_anniversary') {
        title = 'Happy Anniversary!';
      } else if (wish.type === 'kids_birthday') {
        title = `Birthday wishes for ${wish.kid_name || 'your little one'}!`;
      } else if (wish.type === 'passport_expiry') {
        message = message
          .replace('{passport_number}', wish.passport_number || '')
          .replace('{expiry_date}', wish.expiry_date || '')
          .replace('{days_left}', wish.days_left ?? '');
        title = `Passport Renewal Reminder — ${wish.days_left} days left`;
      } else {
        title = wish.custom_name || 'Greetings!';
      }
      await axios.post(`${API_URL}/api/wishes/send`, {
        employee_id: wish.employee_id, type: wish.type, title, message,
        ...(wish.threshold != null ? { threshold: wish.threshold } : {}),
      }, { withCredentials: true });
      toast.success(`Wish sent to ${wish.name}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to send wish');
    } finally {
      setSending(null);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/wishes/config`, editConfig, { withCredentials: true });
      toast.success('Wish configuration saved');
      setConfigOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save config');
    }
  };

  const addCustomDay = () => {
    setEditConfig(prev => ({
      ...prev,
      custom_days: [...(prev.custom_days || []), { name: '', date: '', template: '', enabled: true, target_state: '' }],
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wishes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><PartyPopper className="w-7 h-7 text-amber-500" /> Wishes & Greetings</h1>
          <p className="text-muted-foreground">Auto-send personalized wishes to employees on special days</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => { setEditConfig(JSON.parse(JSON.stringify(config))); setConfigOpen(true); }} data-testid="config-wishes-btn">
          <Settings2 className="w-4 h-4" /> Configure
        </Button>
      </div>

      <Tabs defaultValue="today">
        <TabsList>
          <TabsTrigger value="today" className="gap-2"><PartyPopper className="w-4 h-4" /> Today's Wishes</TabsTrigger>
        </TabsList>
        <TabsContent value="today" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Wishes for Today — {todayWishes?.date}</CardTitle>
              <CardDescription>{todayWishes?.wishes?.length || 0} wish(es) due today. {config?.auto_send ? 'Auto-send is ON' : 'Auto-send is OFF'}</CardDescription>
            </CardHeader>
            <CardContent>
              {todayWishes?.wishes?.length > 0 ? (
                <div className="space-y-3">
                  {todayWishes.wishes.map((w, i) => (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${w.type === 'passport_expiry' && w.days_left <= 30 ? 'bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-900' : w.type === 'passport_expiry' ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-300 dark:border-amber-900' : 'bg-muted/20'}`} data-testid={`wish-${i}`}>
                      <div>
                        <span className="font-medium text-sm">{w.name} <Badge variant="outline" className="ml-1 text-xs">{w.department}</Badge></span>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {w.type === 'birthday' && 'Birthday'}
                          {w.type === 'marriage_anniversary' && 'Marriage Anniversary'}
                          {w.type === 'kids_birthday' && `Kid's Birthday${w.kid_name ? ` (${w.kid_name})` : ''}`}
                          {w.type === 'passport_expiry' && (
                            <span className={w.days_left <= 30 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-amber-600 dark:text-amber-400 font-medium'}>
                              Passport expires in {w.days_left} days ({w.expiry_date}) · {w.passport_number}
                            </span>
                          )}
                          {w.type === 'custom' && (w.custom_name || 'Special Day')}
                          {w.state && w.type !== 'passport_expiry' && <span className="ml-2 text-violet-500">({w.state})</span>}
                        </p>
                      </div>
                      {w.sent ? (
                        <Badge className="badge-success gap-1"><Check className="w-3 h-3" /> Sent</Badge>
                      ) : (
                        <Button size="sm" className="gap-1" onClick={() => handleSendWish(w)} disabled={sending === w.employee_id + w.type} data-testid={`send-wish-${i}`}>
                          {sending === w.employee_id + w.type ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No wishes due today. Employees can add their dates in My Profile &rarr; Personal Dates.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Wish Configuration</DialogTitle>
            <DialogDescription>Configure auto-greetings and custom special days</DialogDescription>
          </DialogHeader>
          {editConfig && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Auto-send wishes</Label>
                <Switch checked={editConfig.auto_send} onCheckedChange={(v) => setEditConfig({ ...editConfig, auto_send: v })} data-testid="auto-send-switch" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Standard Wishes</Label>
                {editConfig.wish_types?.map((wt, idx) => (
                  <div key={idx} className="p-3 rounded-lg border space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{wt.label}</span>
                      <Switch checked={wt.enabled} onCheckedChange={(v) => { const types = [...editConfig.wish_types]; types[idx] = { ...types[idx], enabled: v }; setEditConfig({ ...editConfig, wish_types: types }); }} />
                    </div>
                    <Textarea value={wt.template || ''} onChange={(e) => { const types = [...editConfig.wish_types]; types[idx] = { ...types[idx], template: e.target.value }; setEditConfig({ ...editConfig, wish_types: types }); }} rows={2} className="text-sm" placeholder="Message template. Use {name} for employee name." />
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Custom / Regional Days</Label>
                  <Button variant="outline" size="sm" className="gap-1" onClick={addCustomDay}><Plus className="w-3 h-3" /> Add</Button>
                </div>
                {editConfig.custom_days?.map((cd, idx) => (
                  <div key={idx} className="p-3 rounded-lg border space-y-2 bg-muted/20">
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={cd.name || ''} onChange={(e) => { const days = [...editConfig.custom_days]; days[idx] = { ...days[idx], name: e.target.value }; setEditConfig({ ...editConfig, custom_days: days }); }} placeholder="Day name (e.g., Navratri)" />
                      <Input type="date" value={cd.date || ''} onChange={(e) => { const days = [...editConfig.custom_days]; days[idx] = { ...days[idx], date: e.target.value }; setEditConfig({ ...editConfig, custom_days: days }); }} />
                    </div>
                    <Input value={cd.target_state || ''} onChange={(e) => { const days = [...editConfig.custom_days]; days[idx] = { ...days[idx], target_state: e.target.value }; setEditConfig({ ...editConfig, custom_days: days }); }} placeholder="Target state (blank = all employees, e.g., Gujarat)" />
                    <Textarea value={cd.template || ''} onChange={(e) => { const days = [...editConfig.custom_days]; days[idx] = { ...days[idx], template: e.target.value }; setEditConfig({ ...editConfig, custom_days: days }); }} rows={2} placeholder="Message in both regional + English" />
                    <Button variant="ghost" size="sm" className="text-destructive gap-1" onClick={() => { const days = editConfig.custom_days.filter((_, i) => i !== idx); setEditConfig({ ...editConfig, custom_days: days }); }}><Trash2 className="w-3 h-3" /> Remove</Button>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfigOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveConfig} data-testid="save-config-btn">Save Configuration</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WishesPage;
